import API_BASE_URL from "../config";

/**
 * Service to handle all backend API requests.
 */
const apiService = {
  /**
   * Fetches the Salesforce access token and instance URL.
   */
  getSalesforceToken: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-salesforce-token`);
      if (!response.ok) {
        throw new Error("Failed to fetch Salesforce token");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getSalesforceToken error:", error);
      throw error;
    }
  },

  /**
   * Fast extraction from email body for the Context tab.
   */
  analyzeEmailContext: async ({ emailBody, messageId, userEmail }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analyze-context`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailBody, messageId, userEmail }),
      });
      if (!response.ok) {
        throw new Error("Context analysis failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.analyzeEmailContext error:", error);
      throw error;
    }
  },

  /**
   * Detailed analysis for the AI Analysis tab.
   */
  analyzeEmailDetailed: async ({ emailBody, messageId, userEmail, caseType, fieldsToExtract, attachments }) => {
    try {
      const payload = {
        emailBody: emailBody || "",
        messageId: messageId || "",
        userEmail: userEmail || "",
        caseType: caseType || "",
        fieldsToExtract: fieldsToExtract || "[]",
        attachments: attachments && attachments.length > 0 ? JSON.stringify(attachments) : "",
      };

      const response = await fetch(`${API_BASE_URL}/api/analyze-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Detailed analysis failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.analyzeEmailDetailed error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to generate cross-sell opportunities from Salesforce data.
   */
  generateCrossSellOpportunities: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cross-sell-opportunities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Cross-sell generation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.generateCrossSellOpportunities error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to match an account in Salesforce.
   */
  matchAccount: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/match-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Account matching failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.matchAccount error:", error);
      throw error;
    }
  },
  /**
   * Calls the backend to search for an account in Salesforce manually.
   */
  searchAccount: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/search-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Account search failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.searchAccount error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to create a case in Salesforce.
   */
  createCase: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-case`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Case creation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.createCase error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to generate a follow-up email draft using AI.
   */
  generateFollowup: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Follow-up generation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.generateFollowup error:", error);
      throw error;
    }
  },

  /**
   * Generates a standalone professional reply for the selected Outlook email.
   * This flow is intentionally independent of Salesforce case creation.
   */
  generateEmailReply: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-email-reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.detail || "Email reply generation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.generateEmailReply error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to schedule a follow-up call in Salesforce.
   */
  scheduleFollowUpCall: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/followup-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to schedule follow-up call");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.scheduleFollowUpCall error:", error);
      throw error;
    }
  },
  /**
   * Calls the backend to create a task in Salesforce.
   */
  createTask: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to create task");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.createTask error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to log an email activity in Salesforce.
   */
  logEmailToSalesforce: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/log-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to log email activity");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.logEmailToSalesforce error:", error);
      throw error;
    }
  },


  /**
   * Fetches all templates (agency + user's personal) from the database.
   */
  getTemplates: async (userEmail = "") => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates?userEmail=${encodeURIComponent(userEmail)}`);
      if (!response.ok) {
        throw new Error("Failed to fetch templates");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getTemplates error:", error);
      throw error;
    }
  },

  /**
   * Fetches all template categories.
   */
  getCategories: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getCategories error:", error);
      throw error;
    }
  },

  /**
   * Creates a new template (personal or agency if admin).
   */
  createTemplate: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to create template");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.createTemplate error:", error);
      throw error;
    }
  },

  /**
   * Updates an existing template by ID.
   */
  updateTemplate: async (id, payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to update template");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.updateTemplate error:", error);
      throw error;
    }
  },

  /**
   * Soft-deletes a template by ID.
   */
  deleteTemplate: async (id, userEmail) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/templates/${id}?userEmail=${encodeURIComponent(userEmail)}`,
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error("Failed to delete template");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.deleteTemplate error:", error);
      throw error;
    }
  },

  /**
   * Checks if the current user email is an admin.
   */
  checkAdmin: async (userEmail) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/is-admin?userEmail=${encodeURIComponent(userEmail)}`);
      if (!response.ok) {
        throw new Error("Failed to check admin status");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.checkAdmin error:", error);
      return { isAdmin: false };
    }
  },

  /**
   * Ensures the current Outlook user exists in the backend users table.
   */
  registerOutlookUser: async ({ userEmail, userName }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/register-outlook-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userEmail: userEmail || "",
          userName: userName || "",
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to register Outlook user");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.registerOutlookUser error:", error);
      throw error;
    }
  },

  /**
   * Fetches the effective admin users for the Outlook plugin.
   */
  getAdminUsers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-users`);
      if (!response.ok) {
        throw new Error("Failed to fetch admin users");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getAdminUsers error:", error);
      throw error;
    }
  },

  /**
   * Fetches Outlook users for admin management.
   */
  getOutlookUsers: async (userEmail) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/outlook-users?userEmail=${encodeURIComponent(userEmail || "")}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch Outlook users");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getOutlookUsers error:", error);
      throw error;
    }
  },

  /**
   * Updates an Outlook user's admin role.
   */
  updateAdminRole: async (userEmail, payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-users/role?userEmail=${encodeURIComponent(userEmail || "")}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update admin role");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.updateAdminRole error:", error);
      throw error;
    }
  },

  /**
   * Fetches Outlook analytics for the current user scope and selected view.
   */
  getAnalytics: async (userEmail, range = "7d", view = "agent") => {
    try {
      const params = new URLSearchParams({
        userEmail: userEmail || "",
        range,
        view,
      });
      const response = await fetch(`${API_BASE_URL}/api/analytics?${params.toString()}`);
      if (!response.ok) {
        throw new Error("Failed to fetch analytics");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getAnalytics error:", error);
      throw error;
    }
  },

  /**
   * Fetches or creates an Outlook user by email.
   */
  getOrCreateUser: async (email, name) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/user?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name || "")}`);
      if (!response.ok) {
        throw new Error("Failed to fetch/create user");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getOrCreateUser error:", error);
      throw error;
    }
  },

  /**
   * Fetches global settings.
   */
  getUserSettings: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/`);
      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getUserSettings error:", error);
      throw error;
    }
  },

  /**
   * Saves user settings.
   */
  saveUserSettings: async (email, settings) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/?email=${encodeURIComponent(email)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save settings");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.saveUserSettings error:", error);
      throw error;
    }
  },
};

export default apiService;
